import { useEffect, useRef, useState } from 'react'
import { useAuth } from '@workos-inc/authkit-react'
import { apiFetch } from '../lib/api'
import type { CatProduct, CatColour, CatVariant, Catalog } from '../lib/catalog-types'
import type { GeneratedFile } from './AddDesignModal'

// ── Exported types ──────────────────────────────────────────────────────
export type PlacementSide   = 'front' | 'front-and-back' | 'full-garment'
export type FrontPosition   = 'pocket-left' | 'pocket-right' | 'large-center' | 'large-center-top'
export type BackPosition    = 'large-center' | 'large-center-top' | 'small-upper-center'
export type DesignMode      = 'manual' | 'automated'

export interface AutomatedDesignResult {
  productSlug: string
  colourId: string
  logoFile: GeneratedFile
  logoLocalUrl: string | null
  placement: PlacementSide
  frontPosition: FrontPosition
  backPosition: BackPosition | null
  sizes: Record<string, number>
}

export interface ManualChoiceResult {
  productSlug: string
  colourId: string | null
  placement: PlacementSide | null
}

// ── Step order ──────────────────────────────────────────────────────────
type Step = 'garment' | 'sides' | 'colour' | 'logo' | 'front-pos' | 'back-pos' | 'mockup' | 'sizes' | 'review'

const STEP_LABELS: Record<Step, string> = {
  garment:       'Select garment',
  sides:         'Select print sides',
  colour:        'Select garment colour',
  logo:          'Your design',
  'front-pos':   'Front logo placement',
  'back-pos':    'Back logo placement',
  mockup:        'Design proof',
  sizes:         'Select sizes',
  review:        'Review & confirm',
}

function buildSteps(p: PlacementSide | null): Step[] {
  const steps: Step[] = ['garment', 'sides', 'colour', 'logo']
  if (p === 'front' || p === 'front-and-back') steps.push('front-pos')
  if (p === 'front-and-back') steps.push('back-pos')
  steps.push('mockup', 'sizes', 'review')
  return steps
}

function estimatedTotal(p: PlacementSide | null): number {
  return buildSteps(p).length
}

// ── Helpers ─────────────────────────────────────────────────────────────

function isColourDark(hex: string): boolean {
  const h = hex.replace('#', '')
  if (h.length !== 6) return true
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b < 140
}

type AiBox = { left: number; top: number; width: number; height: number }

// Print area within the SVG garment silhouette (fractions of 52×60 viewBox)
const SVG_BOX: AiBox = { left: 10 / 52, top: 13 / 60, width: 32 / 52, height: 42 / 60 }

function logoOverlayCss(
  pos: FrontPosition | BackPosition | null,
  box: AiBox,
): React.CSSProperties {
  if (!pos) return { display: 'none' }
  const { left: bL, top: bT, width: bW, height: bH } = box
  let lW: number, lLeft: number, lTop: number

  switch (pos) {
    case 'pocket-left':
      lW    = bW * 0.26
      lLeft = bL + bW * 0.04
      lTop  = bT + bH * 0.04
      break
    case 'pocket-right':
      lW    = bW * 0.26
      lLeft = bL + bW - lW - bW * 0.04
      lTop  = bT + bH * 0.04
      break
    case 'large-center':
      lW    = bW * 0.80
      lLeft = bL + (bW - lW) / 2
      lTop  = bT + (bH - bH * 0.80) / 2
      break
    case 'large-center-top':
      lW    = bW * 0.80
      lLeft = bL + (bW - lW) / 2
      lTop  = bT + bH * 0.08
      break
    case 'small-upper-center':
      lW    = bW * 0.42
      lLeft = bL + (bW - lW) / 2
      lTop  = bT + bH * 0.04
      break
    default:
      return { display: 'none' }
  }
  return {
    position: 'absolute',
    left:      `${lLeft * 100}%`,
    top:       `${lTop  * 100}%`,
    width:     `${lW    * 100}%`,
    maxHeight: `${bH * 0.82 * 100}%`,
    objectFit: 'contain',
  }
}

// ── Live garment preview (shared by front-pos, back-pos, mockup) ─────────

function LiveGarmentPreview({ product, selectedColour, logoLocalUrl, side, position, size = 'md' }: {
  product: CatProduct
  selectedColour: CatColour | undefined
  logoLocalUrl: string | null
  side: 'front' | 'back'
  position: FrontPosition | BackPosition | null
  size?: 'sm' | 'md' | 'lg'
}) {
  const location = product.printLocations.find((l) =>
    side === 'back'
      ? l.name.toLowerCase().includes('back')
      : l.name.toLowerCase().includes('front')
  ) ?? (side === 'front' ? product.printLocations[0] : undefined)

  const box    = location?.aiReferenceBox ?? null
  const refUrl = location?.aiReferenceUrl ?? null
  const maxW   = size === 'sm' ? 'max-w-[120px]' : size === 'lg' ? 'max-w-full' : 'max-w-[200px]'

  // Use actual garment photo: aiReferenceUrl first, then product mockupUrl, sideColour fallback, SVG last resort
  const bgImgUrl  = refUrl ?? product.mockupUrl ?? product.sideColourMockups?.[0]?.url ?? null
  const effectBox = box ?? SVG_BOX   // fall back to SVG estimates if no aiReferenceBox

  if (bgImgUrl) {
    return (
      <div className={`relative rounded-lg overflow-hidden border border-border bg-surface mx-auto ${maxW}`}
        style={{ aspectRatio: '3/4' }}>
        <img src={bgImgUrl} alt="Garment" className="absolute inset-0 w-full h-full object-cover" />
        {logoLocalUrl && (
          <img src={logoLocalUrl} alt="Your design" className="absolute object-contain"
            style={logoOverlayCss(position, effectBox)} />
        )}
      </div>
    )
  }

  // SVG silhouette — only reached when no garment photo at all
  return (
    <div className={`relative mx-auto ${maxW}`} style={{ aspectRatio: '52/60' }}>
      <svg viewBox="0 0 52 60" className="w-full h-full" style={{ color: selectedColour?.hex ?? '#999' }}>
        <path
          d="M8 8 Q5 4 2 10 L2 26 Q2 30 6 30 L6 56 Q6 58 8 58 L44 58 Q46 58 46 56 L46 30 Q50 30 50 26 L50 10 Q47 4 44 8 L36 3 Q31 1 26 1 Q21 1 16 3 Z"
          fill="currentColor" stroke="currentColor" strokeWidth="0.4" opacity="0.9" />
        <rect
          x={SVG_BOX.left * 52} y={SVG_BOX.top * 60}
          width={SVG_BOX.width * 52} height={SVG_BOX.height * 60}
          fill="none" stroke="white" strokeWidth="0.5" strokeDasharray="2 1.5" opacity="0.25" />
      </svg>
      {logoLocalUrl && (
        <img src={logoLocalUrl} alt="Your design" className="absolute object-contain"
          style={logoOverlayCss(position, SVG_BOX)} />
      )}
    </div>
  )
}

// ── Main component ──────────────────────────────────────────────────────
export default function AutomatedDesignWizard({
  open, onClose, product, catalog, onComplete, onChooseManual,
  resetKey = 0, onStepChange,
}: {
  open: boolean
  onClose: () => void
  product: CatProduct
  catalog: Catalog
  onComplete: (result: AutomatedDesignResult) => void
  onChooseManual: (partial: ManualChoiceResult) => void
  resetKey?: number
  onStepChange?: (current: number, total: number, stepName: string) => void
}) {
  const { getAccessToken } = useAuth()

  const [step, setStep]                       = useState<Step>('garment')
  const [selectedProduct, setSelectedProduct] = useState<CatProduct>(product)

  const [colourId, setColourId]               = useState<string | null>(product.colours[0]?.id ?? null)
  const [logoTab, setLogoTab]                 = useState<'ai' | 'upload' | 'studio'>('ai')
  const [logoFile, setLogoFile]               = useState<GeneratedFile | null>(null)
  const [logoLocalUrl, setLogoLocalUrl]       = useState<string | null>(null)
  const [placement, setPlacement]             = useState<PlacementSide | null>(null)
  const [frontPos, setFrontPos]               = useState<FrontPosition | null>(null)
  const [backPos, setBackPos]                 = useState<BackPosition | null>(null)
  const [sizes, setSizes]                     = useState<Record<string, number>>({})

  const [aiPrompt, setAiPrompt]               = useState('')
  const [aiState, setAiState]                 = useState<'idle' | 'busy' | 'done' | 'error'>('idle')
  const [aiError, setAiError]                 = useState<string | null>(null)
  const [aiProgress, setAiProgress]           = useState(0)
  const [aiStage, setAiStage]                 = useState('Generating logo…')
  const aiStartRef                            = useRef<number>(0)

  const [showRegen, setShowRegen]             = useState(false)
  const [regenPrompt, setRegenPrompt]         = useState('')

  const [refDataUrl, setRefDataUrl]           = useState<string | null>(null)
  const [refName, setRefName]                 = useState<string | null>(null)
  const [primaryHex, setPrimaryHex]           = useState<string | null>(null)
  const [secondaryHex, setSecondaryHex]       = useState<string | null>(null)
  const refInputRef                           = useRef<HTMLInputElement | null>(null)

  const [uploadBusy, setUploadBusy]           = useState(false)
  const [uploadError, setUploadError]         = useState<string | null>(null)
  const [dragging, setDragging]               = useState(false)
  const hiddenFileRef                         = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && aiState !== 'busy' && !uploadBusy) onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose, aiState, uploadBusy])

  // Reset all wizard state when resetKey changes (incremented by parent after
  // completion) so the next run starts fresh. Closing mid-flow no longer resets
  // state — progress is preserved so the user can resume via the floating button.
  useEffect(() => {
    setStep('garment'); setSelectedProduct(product)
    setColourId(product.colours[0]?.id ?? null)
    setLogoTab('ai'); setLogoFile(null); setLogoLocalUrl(null)
    setPlacement(null); setFrontPos(null); setBackPos(null); setSizes({})
    setAiPrompt(''); setAiState('idle'); setAiError(null)
    setRefDataUrl(null); setRefName(null); setPrimaryHex(null); setSecondaryHex(null)
    setShowRegen(false); setRegenPrompt('')
    setUploadBusy(false); setUploadError(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey])

  // Report step progress to parent so it can show a floating resume indicator.
  useEffect(() => {
    if (!onStepChange) return
    const s = buildSteps(placement)
    onStepChange(s.indexOf(step) + 1, s.length, STEP_LABELS[step])
  }, [step, placement, onStepChange])

  useEffect(() => {
    if (aiState !== 'busy') return
    aiStartRef.current = Date.now()
    setAiProgress(0); setAiStage('Generating logo…')
    const id = window.setInterval(() => {
      const sec = (Date.now() - aiStartRef.current) / 1000
      let pct: number; let stage: string
      if (sec < 25)      { pct = (sec / 25) * 80;                     stage = 'Generating logo…' }
      else if (sec < 30) { pct = 80 + ((sec - 25) / 5) * 12;          stage = 'Removing background…' }
      else               { pct = 92 + Math.min(3, (sec - 30) * 0.15); stage = 'Final touches…' }
      setAiProgress(Math.min(95, pct)); setAiStage(stage)
    }, 200)
    return () => window.clearInterval(id)
  }, [aiState])

  if (!open) return null

  const selectedColour = selectedProduct.colours.find((c) => c.id === colourId) ?? selectedProduct.colours[0]
  const sizeVariants   = selectedProduct.variants.filter((v) => v.size !== null)
  const totalQty       = Object.values(sizes).reduce((a, b) => a + b, 0)
  const steps          = buildSteps(placement)
  const stepIdx        = steps.indexOf(step)
  const totalStepCount = estimatedTotal(placement)
  const busy           = aiState === 'busy' || uploadBusy

  function goNext() { const i = steps.indexOf(step); if (i < steps.length - 1) setStep(steps[i + 1]!) }
  function goPrev() { const i = steps.indexOf(step); if (i > 0) setStep(steps[i - 1]!) }

  function handleProductChange(p: CatProduct) {
    setSelectedProduct(p); setColourId(p.colours[0]?.id ?? null); setSizes({})
  }

  async function generate(prompt: string) {
    if (!prompt.trim()) return
    setAiState('busy'); setAiError(null); setLogoFile(null); setLogoLocalUrl(null); setShowRegen(false)
    try {
      const f = await apiFetch<GeneratedFile>(
        getAccessToken as unknown as () => Promise<string | undefined>,
        '/api/files/generate-design',
        {
          method: 'POST',
          body: JSON.stringify({
            prompt:                prompt.trim(),
            garmentColourHex:      selectedColour?.hex     || undefined,
            garmentColourName:     selectedColour?.name    || undefined,
            referenceImageDataUrl: refDataUrl              || undefined,
            primaryColourHex:      primaryHex              || undefined,
            secondaryColourHex:    secondaryHex            || undefined,
          }),
        },
      )
      setAiProgress(100); setAiStage('Ready')
      setLogoFile(f); setLogoLocalUrl(f.url); setAiState('done')
    } catch (e) {
      setAiState('error'); setAiError((e as Error).message || 'Generation failed. Try again.')
    }
  }

  async function handleFile(file: File) {
    if (file.size > 10 * 1024 * 1024) { setUploadError('Max 10 MB'); return }
    setUploadBusy(true); setUploadError(null); setLogoFile(null); setShowRegen(false)
    const reader = new FileReader()
    reader.onload = () => {
      const dataUrl = reader.result as string
      setLogoLocalUrl(dataUrl)
      const probe = new Image()
      probe.onload = async () => {
        try {
          const f = await apiFetch<GeneratedFile>(
            getAccessToken as unknown as () => Promise<string | undefined>,
            '/api/files',
            {
              method: 'POST',
              body: JSON.stringify({
                originalName: file.name, mimeType: file.type || 'image/png',
                base64: dataUrl, kind: 'artwork',
                widthPx: probe.naturalWidth, heightPx: probe.naturalHeight,
              }),
            },
          )
          setLogoFile(f); setUploadBusy(false)
        } catch (e) {
          setUploadError((e as Error).message || 'Upload failed.'); setUploadBusy(false)
        }
      }
      probe.src = dataUrl
    }
    reader.readAsDataURL(file)
  }

  function pickReference(file: File) {
    if (!file.type.startsWith('image/')) return
    const r = new FileReader()
    r.onload = () => { if (typeof r.result === 'string') { setRefDataUrl(r.result); setRefName(file.name) } }
    r.readAsDataURL(file)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault(); setDragging(false)
    const file = e.dataTransfer.files?.[0]; if (file) void handleFile(file)
  }

  function handleComplete() {
    if (!colourId || !logoFile || !placement) return
    if (placement !== 'full-garment' && !frontPos) return
    onComplete({
      productSlug: selectedProduct.slug, colourId, logoFile, logoLocalUrl,
      placement, frontPosition: frontPos ?? 'large-center',
      backPosition: placement === 'front-and-back' ? (backPos ?? 'large-center') : null,
      sizes,
    })
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div className="bg-cream rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden border border-brick/40">

        {/* Header */}
        <div className="bg-brick text-white px-5 py-3 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white text-brick text-xs font-bold shrink-0">
              {stepIdx + 1}
            </span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">
                Guided setup · Step {stepIdx + 1} of {totalStepCount}
              </div>
              <div className="text-sm font-semibold truncate">{STEP_LABELS[step]}</div>
            </div>
          </div>
          <button onClick={onClose} disabled={busy}
            className="w-8 h-8 rounded hover:bg-white/15 disabled:opacity-40 flex items-center justify-center" aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Progress bar */}
        <div className="h-1 bg-brick/10 shrink-0">
          <div className="h-full bg-brick transition-[width] duration-300 ease-out"
            style={{ width: `${((stepIdx + 1) / totalStepCount) * 100}%` }} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-5">
          {step === 'garment'     && <StepGarment catalog={catalog} selectedProduct={selectedProduct} onSelect={handleProductChange} />}
          {step === 'sides'       && <StepPlacement placement={placement} setPlacement={setPlacement} product={selectedProduct} />}
          {step === 'colour'      && <StepColour colours={selectedProduct.colours} colourId={colourId} setColourId={setColourId} />}
          {step === 'logo'        && (
            <StepLogo
              tab={logoTab} setTab={setLogoTab}
              aiPrompt={aiPrompt} setAiPrompt={setAiPrompt}
              aiState={aiState} aiError={aiError}
              aiProgress={aiProgress} aiStage={aiStage}
              onGenerate={() => void generate(showRegen ? regenPrompt : aiPrompt)}
              logoFile={logoFile} logoLocalUrl={logoLocalUrl}
              showRegen={showRegen} setShowRegen={setShowRegen}
              regenPrompt={regenPrompt} setRegenPrompt={setRegenPrompt}
              onRegenerate={() => void generate(regenPrompt || aiPrompt)}
              onClearLogo={() => { setLogoFile(null); setLogoLocalUrl(null); setAiState('idle'); setAiError(null) }}
              refDataUrl={refDataUrl} refName={refName}
              refInputRef={refInputRef}
              onPickReference={pickReference}
              onClearRef={() => { setRefDataUrl(null); setRefName(null) }}
              primaryHex={primaryHex} setPrimaryHex={setPrimaryHex}
              secondaryHex={secondaryHex} setSecondaryHex={setSecondaryHex}
              dragging={dragging} setDragging={setDragging}
              uploadBusy={uploadBusy} uploadError={uploadError}
              onDrop={handleDrop}
              onPickFile={(f) => void handleFile(f)}
              hiddenFileRef={hiddenFileRef}
              selectedColour={selectedColour}
              onSelectStudio={(f, url) => { setLogoFile(f); setLogoLocalUrl(url) }}
            />
          )}
          {step === 'front-pos'   && (
            <StepFrontPosition
              frontPos={frontPos} setFrontPos={setFrontPos}
              product={selectedProduct} selectedColour={selectedColour} logoLocalUrl={logoLocalUrl}
            />
          )}
          {step === 'back-pos'    && (
            <StepBackPosition
              backPos={backPos} setBackPos={setBackPos}
              product={selectedProduct} selectedColour={selectedColour} logoLocalUrl={logoLocalUrl}
            />
          )}
          {step === 'mockup'      && (
            <StepMockup
              product={selectedProduct} selectedColour={selectedColour} logoLocalUrl={logoLocalUrl}
              placement={placement!}
              frontPos={frontPos} backPos={backPos}
              onOpenInEditor={handleComplete}
            />
          )}
          {step === 'sizes'       && <StepSizes variants={sizeVariants} sizes={sizes} setSizes={setSizes} totalQty={totalQty} />}
          {step === 'review'      && (
            <StepReview
              product={selectedProduct} selectedColour={selectedColour} logoLocalUrl={logoLocalUrl}
              placement={placement!} frontPos={frontPos!} backPos={backPos}
              sizes={sizes} totalQty={totalQty}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-brick/20 bg-cream/80 px-5 py-3 flex items-center justify-between gap-3 shrink-0">
          <div>
            {stepIdx > 0 && (
              <button type="button" onClick={goPrev} disabled={busy}
                className="px-3 py-1.5 rounded border border-brick/40 text-brick text-xs font-semibold uppercase tracking-wide hover:bg-brick-tint disabled:opacity-40">
                ← Back
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {step !== 'review' && (
              <button type="button" onClick={onClose} disabled={busy}
                className="px-3 py-1.5 rounded text-ink-3 text-xs font-semibold uppercase tracking-wide hover:text-ink hover:bg-white disabled:opacity-40">
                Cancel
              </button>
            )}
            {step === 'garment'   && <NextBtn onClick={goNext} label="Next →" />}
            {step === 'sides'     && <NextBtn onClick={goNext} disabled={!placement} label="Next →" />}
            {step === 'colour'    && <NextBtn onClick={goNext} disabled={!colourId} label="Next →" />}
            {step === 'logo'      && logoFile && !showRegen && <NextBtn onClick={goNext} label="Use this design →" />}
            {step === 'front-pos' && <NextBtn onClick={goNext} disabled={!frontPos} label="Next →" />}
            {step === 'back-pos'  && <NextBtn onClick={goNext} disabled={!backPos} label="Next →" />}
            {step === 'mockup'    && <NextBtn onClick={goNext} label="Looks good →" />}
            {step === 'sizes'     && <NextBtn onClick={goNext} disabled={totalQty === 0} label="Review order →" />}
            {step === 'review'    && (
              <button type="button" onClick={handleComplete}
                disabled={!colourId || !logoFile || !placement || (placement !== 'full-garment' && !frontPos)}
                className="px-5 py-2 rounded bg-brick text-white text-xs font-bold uppercase tracking-wide hover:bg-brick-dark disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/>
                </svg>
                Apply &amp; Add to Cart
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Shared ──────────────────────────────────────────────────────────────

function NextBtn({ onClick, disabled = false, label }: { onClick: () => void; disabled?: boolean; label: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="px-4 py-1.5 rounded bg-brick text-white text-xs font-semibold uppercase tracking-wide hover:bg-brick-dark disabled:opacity-40 disabled:cursor-not-allowed">
      {label}
    </button>
  )
}

// ── Step: Garment ───────────────────────────────────────────────────────

function StepGarment({ catalog, selectedProduct, onSelect }: {
  catalog: Catalog; selectedProduct: CatProduct; onSelect: (p: CatProduct) => void
}) {
  const garments = catalog.products.filter((p) => p.kind === 'garment')
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">Choose the garment you'd like to design on.</p>
      {garments.length === 0 && <p className="text-sm text-ink-3 italic">No garments available.</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {garments.map((p) => {
          const active = p.id === selectedProduct.id
          return (
            <button key={p.id} type="button" onClick={() => onSelect(p)}
              className={['rounded-lg border-2 p-3 text-left transition-all flex flex-col gap-2',
                active ? 'border-brick bg-brick-tint/40' : 'border-border bg-white hover:border-brick/40'].join(' ')}>
              {(p.mockupUrl ?? p.sideColourMockups?.[0]?.url)
                ? <img src={p.mockupUrl ?? p.sideColourMockups?.[0]?.url} alt={p.name} className="w-full aspect-square object-contain rounded bg-surface" />
                : <div className="w-full aspect-square rounded bg-surface flex items-center justify-center">
                    <svg viewBox="0 0 52 60" className="w-12 h-14 text-ink-3" fill="none">
                      <path d="M8 8 Q5 4 2 10 L2 26 Q2 30 6 30 L6 56 Q6 58 8 58 L44 58 Q46 58 46 56 L46 30 Q50 30 50 26 L50 10 Q47 4 44 8 L36 3 Q31 1 26 1 Q21 1 16 3 Z"
                        fill="currentColor" opacity="0.12" stroke="currentColor" strokeWidth="1.5"/>
                    </svg>
                  </div>
              }
              <div>
                <div className="text-xs font-semibold text-ink leading-tight">{p.name}</div>
                {p.colours.length > 0 && <div className="text-[10px] text-ink-3 mt-0.5">{p.colours.length} colour{p.colours.length !== 1 ? 's' : ''}</div>}
              </div>
              {active && <span className="text-[10px] font-bold uppercase tracking-wide text-brick">Selected ✓</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Step: Design mode ───────────────────────────────────────────────────

function StepDesignMode({ onSelect }: { onSelect: (mode: DesignMode) => void }) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">How would you like to add your design?</p>
      <div className="grid md:grid-cols-2 gap-4">
        <button onClick={() => onSelect('automated')}
          className="text-left rounded-lg border-2 border-brick/30 bg-white p-5 hover:border-brick hover:bg-brick-tint/30 transition-all group">
          <div className="w-10 h-10 rounded-full bg-brick text-white flex items-center justify-center mb-3 shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l2.09 6.26L21 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
            </svg>
          </div>
          <div className="font-heading font-bold text-xl text-ink uppercase mb-1">AI Assisted</div>
          <div className="text-xs text-ink-3 mb-4 leading-relaxed">Generate a logo with AI or upload artwork, then we'll guide you through placement and proof.</div>
          <ul className="space-y-1.5 text-xs text-ink-2 mb-4">
            {['Generate a logo with AI','Or upload your own artwork','Set front & back placement','Preview proof before confirming'].map((s) => (
              <li key={s} className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-brick/10 text-brick flex items-center justify-center shrink-0">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                </span>{s}
              </li>
            ))}
          </ul>
          <div className="text-[11px] font-bold uppercase tracking-wide text-brick group-hover:underline flex items-center gap-1">
            Continue guided
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </div>
        </button>

        <button onClick={() => onSelect('manual')}
          className="text-left rounded-lg border-2 border-border bg-white p-5 hover:border-ink hover:bg-surface transition-all group">
          <div className="w-10 h-10 rounded-full bg-ink text-white flex items-center justify-center mb-3 shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
            </svg>
          </div>
          <div className="font-heading font-bold text-xl text-ink uppercase mb-1">Manual</div>
          <div className="text-xs text-ink-3 mb-4 leading-relaxed">Jump straight into the designer. Full control over every detail.</div>
          <ul className="space-y-1.5 text-xs text-ink-2 mb-4">
            {['Upload artwork or generate with AI','Full canvas drag-and-drop control','Multi-location printing','Precise size & DPI readout'].map((s) => (
              <li key={s} className="flex items-center gap-2">
                <span className="w-4 h-4 rounded-full bg-ink/10 text-ink-3 flex items-center justify-center shrink-0">
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3.5} strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                </span>{s}
              </li>
            ))}
          </ul>
          <div className="text-[11px] font-bold uppercase tracking-wide text-ink-3 group-hover:text-ink group-hover:underline flex items-center gap-1">
            Open builder
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </div>
        </button>
      </div>
    </div>
  )
}

// ── Step: Colour ────────────────────────────────────────────────────────

function StepColour({ colours, colourId, setColourId }: {
  colours: CatColour[]; colourId: string | null; setColourId: (id: string) => void
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">Choose the colour of the garment you'd like to print on.</p>
      {colours.length === 0 && <p className="text-sm text-ink-3 italic">No colours configured for this product.</p>}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {colours.map((c) => {
          const active = c.id === colourId
          return (
            <button key={c.id} type="button" onClick={() => setColourId(c.id)}
              className={['rounded-lg border-2 p-3 text-left transition-all',
                active ? 'border-brick bg-brick-tint/40' : 'border-border bg-white hover:border-brick/40'].join(' ')}>
              <span className="block w-8 h-8 rounded-full border border-black/10 mb-2 shadow-sm" style={{ background: c.hex }} />
              <span className="text-xs font-semibold text-ink leading-tight block">{c.name}</span>
              <span className="text-[10px] font-mono text-ink-3">{c.hex}</span>
              {active && <span className="mt-1 block text-[10px] font-bold uppercase tracking-wide text-brick">Selected</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Step: Logo ──────────────────────────────────────────────────────────

function StepLogo({
  tab, setTab, aiPrompt, setAiPrompt, aiState, aiError, aiProgress, aiStage,
  onGenerate, logoFile, logoLocalUrl,
  showRegen, setShowRegen, regenPrompt, setRegenPrompt, onRegenerate, onClearLogo,
  refDataUrl, refName, refInputRef, onPickReference, onClearRef,
  primaryHex, setPrimaryHex, secondaryHex, setSecondaryHex,
  dragging, setDragging, uploadBusy, uploadError,
  onDrop, onPickFile, hiddenFileRef, selectedColour, onSelectStudio,
}: {
  tab: 'ai' | 'upload' | 'studio'; setTab: (t: 'ai' | 'upload' | 'studio') => void
  aiPrompt: string; setAiPrompt: (s: string) => void
  aiState: 'idle' | 'busy' | 'done' | 'error'
  aiError: string | null; aiProgress: number; aiStage: string
  onGenerate: () => void
  logoFile: GeneratedFile | null; logoLocalUrl: string | null
  showRegen: boolean; setShowRegen: (b: boolean) => void
  regenPrompt: string; setRegenPrompt: (s: string) => void
  onRegenerate: () => void; onClearLogo: () => void
  refDataUrl: string | null; refName: string | null
  refInputRef: React.RefObject<HTMLInputElement>
  onPickReference: (f: File) => void; onClearRef: () => void
  primaryHex: string | null; setPrimaryHex: (h: string | null) => void
  secondaryHex: string | null; setSecondaryHex: (h: string | null) => void
  dragging: boolean; setDragging: (b: boolean) => void
  uploadBusy: boolean; uploadError: string | null
  onDrop: (e: React.DragEvent) => void
  onPickFile: (f: File) => void
  hiddenFileRef: React.RefObject<HTMLInputElement>
  selectedColour: CatColour | undefined
  onSelectStudio: (file: GeneratedFile, url: string) => void
}) {
  const busy = aiState === 'busy' || uploadBusy
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [bgDark, setBgDark] = useState<boolean>(() =>
    selectedColour ? isColourDark(selectedColour.hex) : true
  )
  const previewBg = bgDark ? '#111111' : '#ffffff'

  // ── Done state: logo preview on garment-matched bg + lightbox ──────────
  if (logoLocalUrl && !showRegen) {
    return (
      <div className="space-y-4">

        {/* Fullscreen lightbox */}
        {lightboxOpen && (
          <div className="fixed inset-0 z-[9999] bg-black/92 flex items-center justify-center p-6"
            onClick={() => setLightboxOpen(false)}>
            <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
              <div className="rounded-2xl overflow-hidden shadow-2xl" style={{ background: previewBg }}>
                <img src={logoLocalUrl} alt="Your design" className="w-full object-contain max-h-[72vh] p-8" />
              </div>
              <button onClick={() => setLightboxOpen(false)}
                className="absolute -top-4 -right-4 w-9 h-9 rounded-full bg-white text-ink flex items-center justify-center shadow-xl hover:bg-surface">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
              <p className="text-center text-white/40 text-xs mt-3">Click outside to close</p>
            </div>
          </div>
        )}

        <p className="text-sm text-ink-2">Your logo is ready. Click the preview to see it full-size.</p>

        <div className="flex flex-col items-center gap-3">
          {/* Logo on dark/light bg — click to open lightbox */}
          <button type="button" onClick={() => setLightboxOpen(true)}
            className="relative rounded-xl overflow-hidden border border-border shadow-md cursor-zoom-in group transition-transform hover:scale-[1.02]"
            style={{ background: previewBg, width: 220, height: 220 }}>
            <img src={logoLocalUrl} alt="Your design" className="w-full h-full object-contain p-6" />
            <span className="absolute inset-0 flex items-end justify-end p-2 opacity-0 group-hover:opacity-100 transition-opacity">
              <span className="bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded font-semibold uppercase tracking-wider">
                Enlarge
              </span>
            </span>
          </button>

          {/* Dark / light background toggle */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-ink-3 font-semibold uppercase tracking-wide">Preview on:</span>
            <button type="button" onClick={() => setBgDark(true)}
              title="Dark background"
              className={['w-7 h-7 rounded border-2 transition-all', bgDark ? 'border-brick ring-2 ring-brick/30 scale-110' : 'border-border hover:border-ink/40'].join(' ')}
              style={{ background: '#111' }} />
            <button type="button" onClick={() => setBgDark(false)}
              title="Light background"
              className={['w-7 h-7 rounded border-2 transition-all', !bgDark ? 'border-brick ring-2 ring-brick/30 scale-110' : 'border-border hover:border-ink/40'].join(' ')}
              style={{ background: '#fff' }} />
          </div>

          {selectedColour && (
            <div className="flex items-center gap-2 text-[11px] text-ink-3">
              <span className="w-3.5 h-3.5 rounded-full border border-black/10 shrink-0" style={{ background: selectedColour.hex }} />
              Garment: {selectedColour.name}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 justify-center">
          <button type="button" onClick={() => setShowRegen(true)}
            className="px-3 py-1.5 rounded border border-brick/40 text-brick text-xs font-semibold uppercase tracking-wide hover:bg-brick-tint">
            {tab === 'ai' ? 'Regenerate with feedback' : 'Regenerate with AI'}
          </button>
          <button type="button" onClick={onClearLogo}
            className="px-3 py-1.5 rounded border border-border text-ink-3 text-xs font-semibold uppercase tracking-wide hover:text-red-600 hover:border-red-300">
            {tab === 'upload' ? 'Choose different file' : 'Start over'}
          </button>
        </div>
        <p className="text-center text-[11px] text-ink-3">Use "Use this design →" in the footer to continue.</p>
      </div>
    )
  }

  // ── Regen prompt ────────────────────────────────────────────────────────
  if (showRegen) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-ink-2">Describe what you'd like to change, or rewrite the prompt entirely.</p>
        <textarea rows={4} value={regenPrompt || aiPrompt}
          onChange={(e) => setRegenPrompt(e.target.value)}
          placeholder="e.g. make it bolder, change the style to vintage, use a different colour…"
          className="input !text-sm !py-2" />
        <div className="flex gap-2">
          <button type="button" onClick={() => setShowRegen(false)}
            className="px-3 py-2 rounded border border-brick/40 text-brick text-xs font-semibold uppercase tracking-wide hover:bg-brick-tint">
            ← Cancel
          </button>
          <button type="button" onClick={onRegenerate} disabled={!(regenPrompt || aiPrompt).trim()}
            className="flex-1 px-4 py-2 rounded bg-brick text-white text-xs font-semibold uppercase tracking-wide hover:bg-brick-dark disabled:opacity-40 flex items-center justify-center gap-2">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2l2 7h7l-5.5 4.5L17 21l-5-4-5 4 1.5-7.5L3 9h7l2-7z"/>
            </svg>
            Regenerate
          </button>
        </div>
      </div>
    )
  }

  // ── Generate / upload form ───────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="border-b border-brick/20 flex gap-1 flex-wrap">
        <button type="button" disabled={busy} onClick={() => setTab('ai')}
          className={['flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-bold rounded-t border-b-2 -mb-px',
            tab === 'ai' ? 'border-brick text-brick' : 'border-transparent text-ink-3 hover:text-brick'].join(' ')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2l2 7h7l-5.5 4.5L17 21l-5-4-5 4 1.5-7.5L3 9h7l2-7z"/>
          </svg>
          Generate with AI
          <span className="ml-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-brick-tint text-brick border border-brick/30">New</span>
        </button>
        <button type="button" disabled={busy} onClick={() => setTab('studio')}
          className={['flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-bold rounded-t border-b-2 -mb-px',
            tab === 'studio' ? 'border-brick text-brick' : 'border-transparent text-ink-3 hover:text-brick'].join(' ')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/>
          </svg>
          Studio Artwork
        </button>
        <button type="button" disabled={busy} onClick={() => setTab('upload')}
          className={['flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider font-bold rounded-t border-b-2 -mb-px',
            tab === 'upload' ? 'border-brick text-brick' : 'border-transparent text-ink-3 hover:text-brick'].join(' ')}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
          </svg>
          Upload your own
        </button>
      </div>

      {tab === 'ai' && (
        <div className="space-y-4">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-1.5">What should your design be?</label>
            <textarea rows={3} value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)}
              placeholder='"a roaring lion silhouette with a crown", "vintage surf logo with the word EVRST"'
              disabled={busy} className="input !text-sm !py-2 disabled:opacity-60" />
            <div className="text-[10px] text-ink-3 mt-1 flex justify-between">
              <span>Logo-style, transparent background, print-ready.</span>
              <span className="font-mono">{aiPrompt.length}/600</span>
            </div>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-1.5">
              Reference image <span className="text-ink-4 normal-case tracking-normal font-normal">— optional, style only</span>
            </label>
            <input ref={refInputRef} type="file" accept="image/*" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickReference(f); e.target.value = '' }} />
            {refDataUrl ? (
              <div className="flex items-center gap-3 rounded border border-brick/30 bg-white p-2">
                <img src={refDataUrl} alt="" className="w-12 h-12 object-cover rounded border border-border" />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-ink truncate">{refName}</div>
                  <div className="text-[10px] text-ink-3">Style reference only — not traced.</div>
                </div>
                <button type="button" onClick={onClearRef} disabled={busy}
                  className="text-[11px] font-semibold text-ink-3 hover:text-red-600 px-2 py-1 rounded">Remove</button>
              </div>
            ) : (
              <button type="button" onClick={() => refInputRef.current?.click()} disabled={busy}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded border border-dashed border-brick/40 bg-white text-sm text-ink-2 hover:border-brick hover:text-brick disabled:opacity-50">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                </svg>
                Upload an image for inspiration
              </button>
            )}
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-1.5">
              Brand colours <span className="text-ink-4 normal-case tracking-normal font-normal">— optional</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              <ColourSwatch label="Primary" hex={primaryHex} onChange={setPrimaryHex} disabled={busy} />
              <ColourSwatch label="Secondary" hex={secondaryHex} onChange={setSecondaryHex} disabled={busy} />
            </div>
          </div>
          {selectedColour && (
            <div className="rounded border border-brick/30 bg-brick-tint/40 px-3 py-2 text-[11px] text-ink-2 leading-relaxed">
              Colours will stand out against your <strong className="text-ink">{selectedColour.name}</strong> garment.
            </div>
          )}
          {aiState === 'error' && aiError && (
            <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-900 font-semibold">{aiError}</div>
          )}
          {aiState === 'busy' && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[11px] font-semibold">
                <span className="text-ink">{aiStage}</span>
                <span className="font-mono text-brick">{Math.round(aiProgress)}%</span>
              </div>
              <div className="h-2 rounded-full bg-brick-tint overflow-hidden border border-brick/30">
                <div className="h-full bg-brick transition-[width] duration-200 ease-out" style={{ width: `${aiProgress}%` }} />
              </div>
            </div>
          )}
          <button type="button" onClick={onGenerate} disabled={busy || !aiPrompt.trim()}
            className="w-full px-4 py-2.5 rounded bg-brick text-white text-xs font-semibold uppercase tracking-wide hover:bg-brick-dark disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2">
            {aiState === 'busy' ? (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} className="animate-spin">
                  <circle cx="12" cy="12" r="10" strokeDasharray="50 30"/>
                </svg>
                Generating…
              </>
            ) : (
              <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2l2 7h7l-5.5 4.5L17 21l-5-4-5 4 1.5-7.5L3 9h7l2-7z"/>
                </svg>
                Generate design
              </>
            )}
          </button>
        </div>
      )}

      {tab === 'studio' && (
        <StudioPickerTab onSelect={(f) => onSelectStudio(f, f.url)} />
      )}

      {tab === 'upload' && (
        <div className="space-y-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            onClick={() => hiddenFileRef.current?.click()}
            className={['flex flex-col items-center justify-center text-center cursor-pointer rounded-lg border-2 border-dashed transition-colors px-6 py-10 bg-white',
              dragging ? 'border-brick bg-brick-tint/30' : 'border-brick/40 hover:border-brick hover:bg-brick-tint/20'].join(' ')}>
            {uploadBusy
              ? <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} className="text-brick animate-spin mb-3"><circle cx="12" cy="12" r="10" strokeDasharray="50 30"/></svg>
              : <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" className="text-brick mb-3">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                </svg>
            }
            <div className="text-sm font-semibold text-ink">{uploadBusy ? 'Uploading…' : 'Click to choose a file'}</div>
            {!uploadBusy && <div className="text-xs text-ink-3 mt-1">or drag &amp; drop one here</div>}
            <div className="text-[11px] text-ink-4 mt-3">PNG, JPG, or SVG up to 10 MB · 300 DPI recommended</div>
          </div>
          <input ref={hiddenFileRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onPickFile(f); e.target.value = '' }} />
          {uploadError && <div className="rounded border border-red-300 bg-red-50 px-3 py-2 text-[12px] text-red-900 font-semibold">{uploadError}</div>}
        </div>
      )}
    </div>
  )
}

// ── Studio artwork picker (used inside StepLogo's studio tab) ───────────

interface StudioItem {
  id: string; originalName: string; mimeType: string
  size: number; widthPx: number | null; heightPx: number | null
  url: string; createdAt: string
}

function StudioPickerTab({ onSelect }: { onSelect: (f: GeneratedFile) => void }) {
  const [items, setItems]     = useState<StudioItem[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch]   = useState('')
  const [error, setError]     = useState<string | null>(null)

  useEffect(() => {
    setLoading(true); setError(null)
    const qs = search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''
    fetch(`/api/studio-media${qs}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then((d: { items: StudioItem[] }) => { setItems(d.items ?? []); setLoading(false) })
      .catch((e: Error) => { setError(e.message); setLoading(false) })
  }, [search])

  return (
    <div className="space-y-3">
      <div className="relative">
        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search studio artwork…"
          className="input pl-8 !py-1.5 !text-sm w-full"
        />
      </div>

      {loading && (
        <div className="flex items-center justify-center py-10">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="text-brick animate-spin">
            <circle cx="12" cy="12" r="10" strokeDasharray="50 30"/>
          </svg>
        </div>
      )}

      {error && !loading && (
        <p className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</p>
      )}

      {!loading && !error && items.length === 0 && (
        <p className="text-sm text-ink-3 italic text-center py-8">
          {search ? 'No artwork matches your search.' : 'No studio artwork available yet.'}
        </p>
      )}

      {!loading && items.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-72 overflow-y-auto pr-1">
          {items.map((item) => (
            <button key={item.id} type="button"
              onClick={() => onSelect({ id: item.id, url: item.url, originalName: item.originalName, mimeType: item.mimeType, size: item.size, widthPx: item.widthPx, heightPx: item.heightPx })}
              className="group relative aspect-square rounded-lg border-2 border-border bg-white overflow-hidden hover:border-brick hover:shadow-md transition-all">
              <img src={item.url} alt={item.originalName}
                className="w-full h-full object-contain p-1.5" />
              <span className="absolute inset-0 bg-brick/0 group-hover:bg-brick/10 transition-colors pointer-events-none" />
              <span className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate opacity-0 group-hover:opacity-100 transition-opacity text-center">
                {item.originalName.replace(/\.[^.]+$/, '')}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Step: Placement (sides) ─────────────────────────────────────────────

function StepPlacement({ placement, setPlacement, product }: {
  placement: PlacementSide | null; setPlacement: (p: PlacementSide) => void; product: CatProduct
}) {
  // Only offer sides the product actually has print locations for.
  const hasBack = product.printLocations.some((l) => l.name.toLowerCase().includes('back'))
  const opts: { id: PlacementSide; label: string; sub: string }[] = [
    { id: 'front',          label: 'Front only',   sub: 'Design prints on the front of the garment.' },
    ...(hasBack ? [{ id: 'front-and-back' as PlacementSide, label: 'Front + Back', sub: 'Design printed on both sides of the garment.' }] : []),
  ]
  return (
    <div className="space-y-3">
      <p className="text-sm text-ink-2">Where would you like your design to be printed?</p>
      <div className={`grid gap-4 ${opts.length === 1 ? 'max-w-xs mx-auto' : 'md:grid-cols-2'}`}>
        {opts.map((o) => {
          const active = placement === o.id
          return (
            <button key={o.id} type="button" onClick={() => setPlacement(o.id)}
              className={['text-left rounded-lg border-2 p-4 flex flex-col items-center text-center gap-3 transition-all',
                active ? 'border-brick bg-brick-tint/30' : 'border-border bg-white hover:border-brick/40'].join(' ')}>
              <div className="font-heading font-bold text-lg text-ink uppercase">{o.label}</div>
              <div className="text-xs text-ink-3">{o.sub}</div>
              {active && <span className="text-[10px] font-bold uppercase tracking-wide text-brick">Selected ✓</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Step: Front position ────────────────────────────────────────────────

function StepFrontPosition({ frontPos, setFrontPos, product, selectedColour, logoLocalUrl }: {
  frontPos: FrontPosition | null; setFrontPos: (p: FrontPosition) => void
  product: CatProduct; selectedColour: CatColour | undefined; logoLocalUrl: string | null
}) {
  const opts: { id: FrontPosition; label: string; sub: string }[] = [
    { id: 'pocket-left',      label: 'Small — upper left',    sub: 'Small chest logo, upper left.' },
    { id: 'pocket-right',     label: 'Small — upper right',   sub: 'Small chest logo, upper right.' },
    { id: 'large-center',     label: 'Large — center middle', sub: 'Full-size design centred in the middle of the front. (80% of print width)' },
    { id: 'large-center-top', label: 'Large — center top',    sub: 'Full-size design centred near the top of the front. (80% of print width)' },
  ]
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">Where on the front should your logo sit? The preview updates live as you choose.</p>

      <LiveGarmentPreview
        product={product} selectedColour={selectedColour}
        logoLocalUrl={logoLocalUrl} side="front" position={frontPos} size="md"
      />

      <div className="space-y-2">
        {opts.map((o) => {
          const active = frontPos === o.id
          return (
            <button key={o.id} type="button" onClick={() => setFrontPos(o.id)}
              className={['w-full text-left rounded-lg border-2 px-4 py-3 flex items-center gap-4 transition-all',
                active ? 'border-brick bg-brick-tint/30' : 'border-border bg-white hover:border-brick/40'].join(' ')}>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-base text-ink uppercase">{o.label}</div>
                <div className="text-xs text-ink-3 mt-0.5">{o.sub}</div>
              </div>
              {active
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="text-brick shrink-0">
                    <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                  </svg>
                : <div className="w-4 h-4 rounded-full border-2 border-border shrink-0" />
              }
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Step: Back position ─────────────────────────────────────────────────

function StepBackPosition({ backPos, setBackPos, product, selectedColour, logoLocalUrl }: {
  backPos: BackPosition | null; setBackPos: (p: BackPosition) => void
  product: CatProduct; selectedColour: CatColour | undefined; logoLocalUrl: string | null
}) {
  const opts: { id: BackPosition; label: string; sub: string }[] = [
    { id: 'large-center',       label: 'Large — center middle', sub: 'Full-size design centred in the middle of the back. (80% of print width)' },
    { id: 'large-center-top',   label: 'Large — center top',   sub: 'Full-size design centred near the top of the back. (80% of print width)' },
    { id: 'small-upper-center', label: 'Small — upper center', sub: 'Smaller logo centred near the top of the back.' },
  ]
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">Where on the back should your logo sit? The preview updates live as you choose.</p>

      <LiveGarmentPreview
        product={product} selectedColour={selectedColour}
        logoLocalUrl={logoLocalUrl} side="back" position={backPos} size="md"
      />

      <div className="space-y-2">
        {opts.map((o) => {
          const active = backPos === o.id
          return (
            <button key={o.id} type="button" onClick={() => setBackPos(o.id)}
              className={['w-full text-left rounded-lg border-2 px-4 py-3 flex items-center gap-4 transition-all',
                active ? 'border-brick bg-brick-tint/30' : 'border-border bg-white hover:border-brick/40'].join(' ')}>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-bold text-base text-ink uppercase">{o.label}</div>
                <div className="text-xs text-ink-3 mt-0.5">{o.sub}</div>
              </div>
              {active
                ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" className="text-brick shrink-0">
                    <circle cx="12" cy="12" r="10"/><polyline points="9 12 11 14 15 10"/>
                  </svg>
                : <div className="w-4 h-4 rounded-full border-2 border-border shrink-0" />
              }
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ── Step: Mockup proof ──────────────────────────────────────────────────

function StepMockup({ product, selectedColour, logoLocalUrl, placement, frontPos, backPos, onOpenInEditor }: {
  product: CatProduct; selectedColour: CatColour | undefined; logoLocalUrl: string | null
  placement: PlacementSide
  frontPos: FrontPosition | null
  backPos: BackPosition | null
  onOpenInEditor: () => void
}) {
  const showBack = placement === 'front-and-back'

  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">
        Here's your design proof. Click a side to open it in the editor for fine adjustments, or click <strong className="text-ink">Looks good →</strong> to continue.
      </p>

      <div className={showBack ? 'grid grid-cols-2 gap-4' : 'max-w-[380px] mx-auto'}>

        {/* Front */}
        <button type="button" onClick={onOpenInEditor}
          className="flex flex-col gap-2 items-center group rounded-xl border-2 border-transparent hover:border-brick/50 transition-all p-3 bg-white hover:bg-brick-tint/20">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">Front</div>
          <div className="relative w-full">
            <LiveGarmentPreview
              product={product} selectedColour={selectedColour}
              logoLocalUrl={logoLocalUrl} side="front" position={frontPos} size="lg"
            />
            <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              <span className="bg-brick text-white text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-full shadow-lg">
                Open in editor
              </span>
            </span>
          </div>
        </button>

        {/* Back */}
        {showBack && (
          <button type="button" onClick={onOpenInEditor}
            className="flex flex-col gap-2 items-center group rounded-xl border-2 border-transparent hover:border-brick/50 transition-all p-3 bg-white hover:bg-brick-tint/20">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">Back</div>
            <div className="relative w-full">
              <LiveGarmentPreview
                product={product} selectedColour={selectedColour}
                logoLocalUrl={logoLocalUrl} side="back" position={backPos} size="lg"
              />
              <span className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                <span className="bg-brick text-white text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-full shadow-lg">
                  Open in editor
                </span>
              </span>
            </div>
          </button>
        )}
      </div>

      <p className="text-center text-[11px] text-ink-3">
        Click a side to open in the full editor for fine-tuned adjustments
      </p>
    </div>
  )
}

// ── Step: Sizes ─────────────────────────────────────────────────────────

function StepSizes({ variants, sizes, setSizes, totalQty }: {
  variants: CatVariant[]; sizes: Record<string, number>; setSizes: (s: Record<string, number>) => void; totalQty: number
}) {
  function setSize(sizeName: string, val: number) { setSizes({ ...sizes, [sizeName]: Math.max(0, val) }) }
  if (variants.length === 0) return <p className="text-sm text-ink-3 italic">No size variants configured.</p>
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">How many of each size would you like? Select at least 1 to continue.</p>
      <div className="space-y-2">
        {variants.map((v) => {
          if (!v.size) return null
          const qty = sizes[v.size] ?? 0
          return (
            <div key={v.id} className="flex items-center gap-4 rounded-lg border border-border bg-white px-4 py-3">
              <div className="w-14 shrink-0">
                <div className="font-heading font-bold text-xl text-ink uppercase">{v.size}</div>
                <div className="text-[10px] text-ink-3">${v.price.toFixed(2)}</div>
              </div>
              <div className="flex-1" />
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setSize(v.size!, qty - 1)} disabled={qty === 0}
                  className="w-7 h-7 rounded border border-border bg-surface text-ink-2 font-bold hover:border-brick hover:text-brick flex items-center justify-center">−</button>
                <input type="number" min={0} value={qty} onChange={(e) => setSize(v.size!, parseInt(e.target.value) || 0)}
                  className="w-12 text-center rounded border border-border bg-white px-1 py-1 text-sm font-semibold text-ink focus:outline-none focus:border-gold" />
                <button type="button" onClick={() => setSize(v.size!, qty + 1)}
                  className="w-7 h-7 rounded border border-border bg-surface text-ink-2 font-bold hover:border-brick hover:text-brick flex items-center justify-center">+</button>
              </div>
            </div>
          )
        })}
      </div>
      {totalQty > 0
        ? (
          <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-brick-tint/50 border border-brick/20">
            <span className="text-sm font-semibold text-ink">Total quantity</span>
            <span className="font-heading font-bold text-xl text-brick">{totalQty}</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 rounded border border-amber-300 bg-amber-50 text-[11px] text-amber-800 font-semibold">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            Please select at least 1 item before continuing.
          </div>
        )
      }
    </div>
  )
}

// ── Step: Review ────────────────────────────────────────────────────────

function StepReview({ product, selectedColour, logoLocalUrl, placement, frontPos, backPos, sizes, totalQty }: {
  product: CatProduct; selectedColour: CatColour | undefined; logoLocalUrl: string | null
  placement: PlacementSide; frontPos: FrontPosition; backPos: BackPosition | null
  sizes: Record<string, number>; totalQty: number
}) {
  const showBack = placement === 'front-and-back'
  const posLabel: Record<FrontPosition, string> = {
    'pocket-left': 'Small — upper left', 'pocket-right': 'Small — upper right',
    'large-center': 'Large — center', 'large-center-top': 'Large — center top',
  }
  const backLabel: Record<BackPosition, string> = {
    'large-center': 'Large — center', 'large-center-top': 'Large — center top', 'small-upper-center': 'Small — upper center',
  }
  return (
    <div className="space-y-4">
      <p className="text-sm text-ink-2">Summary of your design choices. Click <strong className="text-ink">Apply &amp; Add to Cart</strong> to continue.</p>

      {/* Garment with artwork */}
      <div className={showBack ? 'grid grid-cols-2 gap-3' : 'max-w-[320px] mx-auto'}>
        <div className="flex flex-col gap-1.5 items-center">
          {showBack && <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">Front</div>}
          <LiveGarmentPreview
            product={product} selectedColour={selectedColour}
            logoLocalUrl={logoLocalUrl} side="front" position={frontPos} size="lg"
          />
        </div>
        {showBack && (
          <div className="flex flex-col gap-1.5 items-center">
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3">Back</div>
            <LiveGarmentPreview
              product={product} selectedColour={selectedColour}
              logoLocalUrl={logoLocalUrl} side="back" position={backPos} size="lg"
            />
          </div>
        )}
      </div>

      {/* Standalone artwork + order details */}
      <div className="flex gap-4 rounded-lg border border-border bg-white p-4">
        {logoLocalUrl && (
          <div className="flex flex-col items-center gap-2 shrink-0">
            <div className="w-16 h-16 rounded-lg border border-border bg-surface flex items-center justify-center overflow-hidden">
              <img src={logoLocalUrl} alt="Your artwork" className="max-w-full max-h-full object-contain p-1" />
            </div>
            <div className="text-[10px] text-center text-ink-3 font-semibold">Your artwork</div>
          </div>
        )}
        <div className="flex-1 min-w-0 text-xs space-y-1.5">
          <Row label="Garment"          value={product.name} />
          <Row label="Colour"           value={selectedColour?.name ?? '—'} />
          <Row label="Print sides"      value={placement === 'front-and-back' ? 'Front + Back' : placement === 'full-garment' ? 'Full Garment' : 'Front only'} />
          {placement !== 'full-garment' && frontPos && <Row label="Front placement" value={posLabel[frontPos]} />}
          {placement === 'front-and-back' && backPos && <Row label="Back placement"  value={backLabel[backPos]} />}
        </div>
      </div>

      {totalQty > 0 && (
        <div className="rounded-lg border border-border bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-ink-3 mb-2">Sizes &amp; quantities</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(sizes).filter(([, q]) => q > 0).map(([size, qty]) => (
              <span key={size} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-surface border border-border text-xs font-semibold text-ink">
                <span className="font-heading uppercase">{size}</span>
                <span className="text-ink-3">×{qty}</span>
              </span>
            ))}
          </div>
          <div className="mt-2 text-right text-xs font-semibold text-ink-2">Total: <span className="text-ink font-bold">{totalQty} pieces</span></div>
        </div>
      )}
      <div className="rounded border border-brick/30 bg-brick-tint/40 px-3 py-2.5 text-[11px] text-ink-2 leading-relaxed">
        Clicking <strong className="text-ink">Apply &amp; Add to Cart</strong> applies your choices to the canvas for final fine-tuning before submitting.
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-ink-3 shrink-0 w-28">{label}</span>
      <span className="font-semibold text-ink">{value}</span>
    </div>
  )
}

function ColourSwatch({ label, hex, onChange, disabled }: {
  label: string; hex: string | null; onChange: (h: string | null) => void; disabled?: boolean
}) {
  const value = hex ?? '#888888'
  return (
    <div className="flex items-center gap-2 rounded border border-border bg-white px-2 py-1.5">
      <label className="relative w-8 h-8 rounded border border-border-2 overflow-hidden cursor-pointer shrink-0">
        <input type="color" value={value} disabled={disabled}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
        <span className="absolute inset-0" style={{ background: hex ?? 'transparent' }} />
        {!hex && <span className="absolute inset-0 flex items-center justify-center text-[10px] text-ink-3 font-bold bg-gradient-to-br from-white via-surface to-border">+</span>}
      </label>
      <div className="flex-1 min-w-0">
        <div className="text-[9px] uppercase tracking-[0.16em] font-bold text-ink-3">{label}</div>
        <div className="font-mono text-xs text-ink truncate">{hex ?? 'Pick a colour'}</div>
      </div>
      {hex && (
        <button type="button" onClick={() => onChange(null)} disabled={disabled}
          className="text-[11px] text-ink-3 hover:text-red-600 font-semibold">×</button>
      )}
    </div>
  )
}
